import { GetServerSideProps, GetStaticProps } from 'next'
import { useRouter } from 'next/router'

import stripUndefined from '../../utils/stripUndefined'
import { getSiteConfig, SiteConfig } from '../../graphql/siteConfig'
import { getLayout, Layout as LayoutType } from '../../graphql/layout'
import { getPage, Page } from '../../graphql/page'
import { SiteLocale } from '../../graphql/datoSchema.generated'
import absolutify from '../../utils/absolutify'
import stringifyQueryString from '../../utils/stringifyQueryString'
import { getPageTitle } from '../[[...slugs]]'
import Layout from '../../components/Layout'
import BlockZone from '../../components/BlockZone'
import blocks from '../../blocks'
import PageSeo from '../../components/PageSeo'
import getVacanciesOverview from '../../utils/getVacanciesOverview'
import { getSiteSettings, SiteSettings } from '../../graphql/siteSettings'
import type { JobsResponse } from '../../utils/getVacancyTypes'

export interface StaticProps {
  siteConfig: SiteConfig
  siteSettings: SiteSettings
  layout: LayoutType
  page: Page
  slug: string
}

// next.js does not allow us to use both getServerSideProps and getStaticProps
// currently. This is coming, so I'll keep the structure in place.
// See https://github.com/vercel/next.js/discussions/11424#discussioncomment-2548409
const getStaticPropsFake: GetStaticProps<
  StaticProps,
  Record<string, never>
> = async ({ locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }
  const [siteConfig, layout, _site] = await Promise.all([
    getSiteConfig(locale as SiteLocale),
    getLayout(locale as SiteLocale),
    getSiteSettings(locale as SiteLocale),
  ])

  // When unset, this site doesn't include a jobs listing
  if (!siteConfig.jobsIndex?.slug) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const page = await getPage(locale as SiteLocale, siteConfig.jobsIndex.slug)

  if (!page) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  return {
    props: stripUndefined({
      siteConfig,
      siteSettings: _site,
      layout,
      page,
      slug: siteConfig.jobsIndex.slug,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}
interface ServerProps {
  vacancies: JobsResponse
  currentPage: number
}

export const getServerSideProps: GetServerSideProps<
  ServerProps,
  Record<string, never>
> = async (context) => {
  const staticProps = await getStaticPropsFake(context)
  if ('redirect' in staticProps) {
    return staticProps
  }
  if ('notFound' in staticProps) {
    // We are not allowed to return `revalidate` in this function
    return {
      notFound: true,
    }
  }

  const { res, query, locale } = context
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const json = await getVacanciesOverview(
    undefined,
    query?.dateSort === 'Date Descending' ? 'Date_Desc' : 'Date_Asc',
  )

  const vacancies =
    query.jobType === undefined || query.jobType === ''
      ? json
      : {
          ...json,
          items: json.items.filter(
            (items) => items.functionArea === query.jobType,
          ),
        }

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=60, stale-while-revalidate=600',
  )

  return {
    props: stripUndefined({
      ...staticProps.props,
      vacancies,
      currentPage: Number(query.page) || 1,
    }),
  }
}

const useCanonical = (slug: string, page: number) => {
  const { locale } = useRouter()
  if (!locale) {
    throw new Error('locale is missing')
  }

  return absolutify(
    page > 1
      ? `/${slug}?${stringifyQueryString(
          stripUndefined({
            page: page > 1 ? page : undefined,
          }),
        )}`
      : `/${slug}`,
    locale as SiteLocale,
  )
}

const JobsIndex = ({
  siteConfig,
  siteSettings,
  layout,
  page,
  slug,
  vacancies,
  currentPage,
}: StaticProps & ServerProps) => {
  const title = getPageTitle(page, siteSettings.globalSeo)
  const canonical = useCanonical(slug, currentPage)

  return (
    <Layout
      siteConfig={siteConfig}
      layout={layout}
      breadcrumbs={[
        {
          key: 'homepage',
          href: '/',
          title: siteConfig.homepageName || 'Home',
        },
        {
          key: 'jobs',
          href: `/${slug}`,
          title: title || '?',
        },
      ]}
    >
      <PageSeo
        page={page}
        seoSettings={siteSettings.globalSeo}
        canonical={canonical}
      />

      <BlockZone
        components={blocks}
        content={page.content}
        data={{
          JobListingSection: {
            currentPage,
            vacancies,
          },
        }}
      />
    </Layout>
  )
}

export default JobsIndex
